import { gtinOf } from './gtin.js'
import { normalizedText } from './normalized-text.js'
import type { Product } from './product.js'

/**
 * A cascata de resolução de `Docs/02-Dominio/ProductMatch.md`: o texto cru de
 * uma linha de nota fiscal virando um `Product` canônico.
 *
 * *"Confundir os dois é o erro clássico que arruína catálogos: você acaba com
 * 'DET YPE 500ML', 'DETERG YPE 500', 'DET. YPÊ 500ML' como três produtos
 * distintos, e o histórico de preço não significa nada."*
 *
 * ## As três primeiras camadas NÃO precisam de IA
 *
 * `barcode`, `exact` e `learned` resolvem sem chamar nada e sem custo. A nota é
 * explícita sobre o que isso vale: *"depois de alguns meses comprando nos mesmos
 * lugares, a maioria dos itens resolve por `learned` e a IA vira exceção"*. É
 * também o que a definição de pronto da fase cobra no item 2 — *"corrigir um
 * vínculo faz o mesmo `rawText` resolver sozinho na nota seguinte"* —, e esse
 * item é inteiramente da camada `learned`.
 *
 * Por isso esta função existe **antes** do `AIProvider`: ela é a parte que
 * carrega o valor, e a que não depende de chave nenhuma.
 *
 * ## Só as três primeiras se aplicam sem perguntar
 *
 * *"`ai` **sempre** passa por confirmação, mesmo com 99% — princípio 5."* O
 * resultado desta função diz `autoApplicable`, e quem decide o que fazer com
 * isso é a tela de revisão. Devolver "aplicável" para uma sugestão de IA seria o
 * princípio 5 violado num campo booleano.
 *
 * ## A ordem é o desenho, e há caso-controle para ela
 *
 * Código de barras ganha de texto porque é identidade, não semelhança. `exact`
 * ganha de `learned` porque um match exato já é a resposta que o aprendizado
 * teria dado. Trocar a ordem produz respostas piores em silêncio — não erros.
 */

export type MatchOrigin = 'barcode' | 'exact' | 'learned' | 'ai' | 'manual'

/**
 * O que já se sabe sobre um texto cru. É o `ProductMatch` gravado, reduzido ao
 * que a resolução usa.
 */
export type KnownMatch = {
  normalizedText: string
  productId: string
  origin: MatchOrigin
  /**
   * A loja e o código interno dela, quando o aprendizado veio de uma linha que
   * os tinha. `null` nos matches aprendidos antes de 2026-09-03 — e eles
   * continuam valendo pelo texto, que é o degrau seguinte.
   *
   * **CNPJ, e não um `Merchant.id`.** A nota traz `merchantName` e
   * `merchantCnpj` e nunca foi ligada à entidade `Merchant`; inventar essa
   * resolução aqui seria construir um passo inteiro para chegar a um
   * identificador que a nota já entrega pronto. O CNPJ **é** o estabelecimento.
   *
   * `merchantName` viaja junto porque é o que a ficha do produto mostra — e um
   * CNPJ na tela não diz a ninguém em que loja o produto se chama assim.
   */
  merchantCnpj: string | null
  merchantName: string | null
  rawCode: string | null
}

/** Uma linha de nota, no mínimo que a cascata precisa. */
export type RawLine = {
  rawText: string
  barcode: string | null
  /**
   * O código interno da loja para aquele item.
   *
   * **É mais estável que o texto**, e é isso que o torna um degrau melhor: o
   * mesmo produto vira `COCA S AC LA 310ML` numa nota e `COCA ZERO LT 310` na
   * outra, conforme quem digitou o cadastro do PDV — o código não muda.
   */
  rawCode: string | null
  /** O CNPJ da loja da nota. Sem ele o código não identifica nada. */
  merchantCnpj: string | null
  /** Só para gravar junto do aprendizado, e aparecer na ficha. */
  merchantName: string | null
}

export type MatchResult = {
  productId: string
  origin: MatchOrigin
  confidence: number
  /**
   * Se pode ser aplicado **sem perguntar**. Falso para `ai`, sempre — princípio
   * 5, e é uma decisão de produto, não de confiança.
   */
  autoApplicable: boolean
}

/** As confianças da tabela da nota. Uma tabela, e não números soltos no código. */
const CONFIDENCE: Record<MatchOrigin, number> = {
  barcode: 1,
  exact: 0.99,
  learned: 0.95,
  // A da IA vem do que ela reportar; este é o piso quando ela não diz.
  ai: 0.5,
  manual: 1,
}

/**
 * Resolve uma linha. `null` quando nada resolve — e aí a linha vai para a IA,
 * ou para a pessoa escolher.
 *
 * **Produto arquivado não resolve**, e o match não é apagado: *"match nunca é
 * apagado quando o produto é arquivado — só deixa de resolver"*. Apagar perderia
 * o aprendizado no dia em que o produto voltasse.
 */
export function resolveLine(
  line: RawLine,
  products: Product[],
  known: KnownMatch[],
): MatchResult | null {
  const ativos = products.filter((p) => p.archivedAt === null)

  // 1. Código de barras: identidade, não semelhança.
  //
  //    **Os dois lados passam por `gtinOf`.** Comparar o texto cru faria o
  //    UPC-A de 12 dígitos da embalagem não casar com o EAN-13 de 13 do
  //    catálogo — e são o mesmo produto, com um zero de diferença. Um código
  //    que não fecha no dígito verificador vira `null` e não casa com nada, que
  //    é o certo: melhor não achar do que achar o produto errado.
  const daLinha = gtinOf(line.barcode)
  if (daLinha !== null) {
    const porCodigo = ativos.find((p) => p.gtin === daLinha)
    if (porCodigo !== undefined) return resultado(porCodigo.id, 'barcode')
  }

  const ativosPorId = new Set(ativos.map((p) => p.id))

  // 2. O código interno da loja. Identidade também, mas só DENTRO da loja.
  //
  //    Vem antes do texto porque é mais estável que ele: o cadastro do PDV
  //    abrevia o nome de um jeito nesta nota e de outro na próxima, e o código
  //    fica igual. E vem antes do `chave === ''` de propósito — uma linha cujo
  //    texto normaliza para vazio (cupom borrado, OCR ruim) ainda resolve pelo
  //    código, que é justamente quando mais precisa.
  const codigo = line.rawCode?.trim() ?? ''
  if (line.merchantCnpj !== null && codigo !== '') {
    const porCodigoDaLoja = known.find(
      (m) =>
        m.merchantCnpj === line.merchantCnpj &&
        m.rawCode === codigo &&
        ativosPorId.has(m.productId),
    )
    if (porCodigoDaLoja !== undefined) return resultado(porCodigoDaLoja.productId, 'learned')
  }

  const chave = normalizedText(line.rawText)
  if (chave === '') return null

  // 3. Texto idêntico ao nome normalizado de um produto do catálogo. Isto é
  //    `exact` — a resposta que o aprendizado teria dado, sem precisar dele.
  const porNome = ativos.find((p) => p.normalizedName === chave)
  if (porNome !== undefined) return resultado(porNome.id, 'exact')

  // 4. O que já foi aprendido para este mesmo texto.
  //
  //    **`manual` e `learned` juntos, e `manual` primeiro.** A nota diz que
  //    *"uma correção manual sempre substitui um match `ai` para o mesmo
  //    texto"*: se os dois existirem, quem manda é a pessoa.
  const aprendidos = known.filter(
    (m) => m.normalizedText === chave && ativosPorId.has(m.productId),
  )
  const manual = aprendidos.find((m) => m.origin === 'manual')
  if (manual !== undefined) return resultado(manual.productId, 'learned')
  const aprendido = aprendidos.find((m) => m.origin === 'learned')
  if (aprendido !== undefined) return resultado(aprendido.productId, 'learned')

  // 5. Um match de IA gravado antes resolve — mas **não se aplica sozinho**.
  const daIA = aprendidos.find((m) => m.origin === 'ai')
  if (daIA !== undefined) return resultado(daIA.productId, 'ai')

  return null
}

function resultado(productId: string, origin: MatchOrigin): MatchResult {
  return {
    productId,
    origin,
    confidence: CONFIDENCE[origin],
    // Só as três primeiras. Ver o cabeçalho — e note que isto NÃO olha a
    // confiança: uma sugestão de IA com 0,99 continua não se aplicando sozinha.
    autoApplicable: origin === 'barcode' || origin === 'exact' || origin === 'learned',
  }
}

/**
 * O `ProductMatch` a gravar quando alguém corrige um vínculo à mão.
 *
 * **É isto que faz o item 2 da definição de pronto valer:** *"corrigir um
 * vínculo faz o mesmo `rawText` resolver sozinho na nota seguinte"*. A chave é o
 * texto normalizado, e não o `rawText` — senão um espaço a mais no cupom da
 * semana seguinte não casaria.
 *
 * Grava **também** a loja e o código interno quando a linha os traz, e é o que
 * alimenta o degrau 2 e a seção "Também conhecido como" da ficha.
 */
export function learnFrom(line: RawLine, productId: string): KnownMatch | null {
  const chave = normalizedText(line.rawText)
  const codigo = line.rawCode?.trim() ?? ''
  const temCodigo = line.merchantCnpj !== null && codigo !== ''

  // Sem texto E sem código não vira chave de nada: casaria com toda outra
  // linha ilegível da nota.
  if (chave === '' && !temCodigo) return null

  return {
    normalizedText: chave,
    productId,
    origin: 'manual',
    merchantCnpj: temCodigo ? line.merchantCnpj : null,
    merchantName: temCodigo ? line.merchantName : null,
    rawCode: temCodigo ? codigo : null,
  }
}

/**
 * Um `KnownMatch` como ele está guardado — com id, para a ficha poder apagar.
 *
 * A ficha chama isto de "também conhecido como", que é o que ele é do ponto de
 * vista de quem olha: os nomes e códigos com que este produto já apareceu nas
 * notas. Ver a seção na tela de produto.
 */
export type ProductAlias = KnownMatch & { id: string }
